// Cómo se dicen y se pintan los datos de la cartera del vendedor y de su ficha. Puro.
import type {
    PortalCustomerOtherStore,
    PortalCustomerPurchase,
    PortalCustomerStatus,
    PortalPurchaseCategory,
    SaleLine,
} from '@/api/services/retail-wizard/contract';
import {
    hasEquipment,
    lineCategoryKey,
    ticketHeadline,
    UNCATEGORIZED_LINE,
    type TicketCategory,
} from '@/shared/patterns/SaleTicket';
import { lineNetCents } from '@/api/services/retail-wizard/soldAmount';
import { ACCOUNT_ONLY_LABEL } from '@/shared/lib/format/accountOnly';
import { SERVICE_CATEGORY } from '@/shared/lib/format/productCategory';
import { ClientLevelEnum } from '@fiado/type-kit/bin/loanScoring/enums/ClientLevelEnum';
import type { DataTableSort } from '@/shared/patterns/DataTable';
import { itemDisplayName } from '@/shared/lib/format/itemDisplayName';
import { daysSinceMx } from '@/lib/dateHelpers';
import { financierKeyOf, financierName } from '../../mysales/lib/mySalesFormat';

/** Lo que dice el chip de estado: el del cliente, o «Solo cuenta» si aún no compra. */
export type PortalChipStatus = PortalCustomerStatus | 'ACCOUNT_ONLY';

export const STATUS_LABEL: Record<PortalChipStatus, string> = {
    ACTIVE: 'Activo',
    REGULAR: 'Regular',
    DORMANT: 'Dormido',
    ACCOUNT_ONLY: ACCOUNT_ONLY_LABEL,
};

/** Fondo y texto del chip de estado, como el mock. */
export const STATUS_CHIP_CLASS: Record<PortalChipStatus, string> = {
    ACTIVE: 'bg-success-soft text-success',
    REGULAR: 'bg-muted text-muted-foreground',
    DORMANT: 'bg-muted text-muted-foreground',
    ACCOUNT_ONLY: 'bg-info-soft text-info',
};

export const STATUS_CHIP_TITLE: Partial<Record<PortalChipStatus, string>> = {
    ACCOUNT_ONLY: 'Abrió su cuenta PCF en tu tienda y aún no compra',
};

const KNOWN_TIERS = new Set<string>(Object.values(ClientLevelEnum));

/** El nivel del scoring; lo que no conozca se trata como sin nivel. */
export function tierOf(clientLevel: string | null): ClientLevelEnum | null {
    return clientLevel !== null && KNOWN_TIERS.has(clientLevel)
        ? (clientLevel as ClientLevelEnum)
        : null;
}

const LEVEL_OUTLINE_CLASS: Partial<Record<ClientLevelEnum, string>> = {
    [ClientLevelEnum.GOLD]: 'border-tier-gold text-tier-gold',
    [ClientLevelEnum.SILVER]: 'border-tier-silver text-tier-silver',
    [ClientLevelEnum.BRONZE]: 'border-tier-bronze text-tier-bronze',
};

/** Contorno y texto del color del metal, como `.nivel-pill` del mock. */
export function levelOutlineClass(level: ClientLevelEnum | null): string {
    return (level && LEVEL_OUTLINE_CLASS[level]) ?? 'border-border text-muted-foreground';
}

/** Pasando estos días sin comprar, la cifra se pinta de alerta y luego de peligro. */
const DAYS_WARNING_AFTER = 30;
const DAYS_DANGER_AFTER = 90;

export function daysLabel(days: number): string {
    return days === 0 ? 'hoy' : `${String(days)}d`;
}

export function daysToneClass(days: number): string {
    if (days > DAYS_DANGER_AFTER) return 'text-danger';
    if (days > DAYS_WARNING_AFTER) return 'text-warning';
    return 'text-muted-foreground';
}

/** «alta 12d» desde que abrió su cuenta; «—» si el alta no llegó. */
export function accountAgeLabel(createdAt: string | null | undefined): string {
    if (!createdAt) return '—';
    return `alta ${String(Math.max(0, daysSinceMx(createdAt)))}d`;
}

/** Iniciales del avatar: primera letra de las dos primeras palabras. */
export function initialsOf(name: string | null): string {
    const words = (name ?? '').trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) return '?';
    return words
        .slice(0, 2)
        .map((word) => word[0])
        .join('')
        .toUpperCase();
}

export type PaymentChipTone = 'cash' | 'surekeep' | 'other';

export interface PaymentChip {
    label: string;
    tone: PaymentChipTone;
}

export const PAYMENT_CHIP_CLASS: Record<PaymentChipTone, string> = {
    cash: 'bg-muted text-muted-foreground',
    surekeep: 'bg-primary/12 text-primary',
    other: 'bg-info-soft text-info',
};

/** Forma de pago de una compra: contado, crédito SureKeep u otra financiera por su nombre. */
export function paymentChipOf(
    purchase: Pick<PortalCustomerPurchase, 'type' | 'externalProvider'>,
): PaymentChip {
    if (purchase.type === 'CASH') return { label: 'Contado', tone: 'cash' };
    if (purchase.type === 'CREDIT' && !purchase.externalProvider) {
        return { label: 'Crédito SK', tone: 'surekeep' };
    }
    return {
        label: financierName(financierKeyOf(purchase.type, purchase.externalProvider)),
        tone: 'other',
    };
}

/** El nombre del producto; las ventas viejas no lo guardaron y cae al SKU (o a «Sin nombre»). */
export function productLabel(
    purchase: Pick<PortalCustomerPurchase, 'productName' | 'sku'>,
): string {
    return purchase.productName ?? itemDisplayName({ sku: purchase.sku });
}

/** Categoría del mix de la ficha: la del catálogo o «Servicio». */
export type PortalMixCategory = PortalPurchaseCategory | typeof SERVICE_CATEGORY;

export type CategoryKey = PortalMixCategory | 'all';

export const CATEGORY_LABEL: Record<PortalMixCategory, string> = {
    PHONE: 'Equipos',
    SIM: 'SIMs',
    ACCESSORY: 'Accesorios',
    MODEM: 'Módems',
    BUNDLE: 'Paquetes',
    [SERVICE_CATEGORY]: 'Servicios',
};

/** Las del mock siempre se ven, aunque estén en cero; las demás solo si hubo compras. */
const ALWAYS_SHOWN: PortalMixCategory[] = ['PHONE', 'SIM', 'ACCESSORY'];

export interface PortalMixEntry {
    category: PortalMixCategory;
    purchasesCount: number;
    totalSoldCents: number;
}

/** Categoría de la línea para el mix; la línea sin producto no entra. */
function mixCategoryOf(line: SaleLine): PortalMixCategory | null {
    const key = lineCategoryKey(line);
    return key === UNCATEGORIZED_LINE ? null : key;
}

/** Categorías de la compra: las de sus líneas; sin líneas, las que mandó el silo. */
function purchaseCategories(purchase: PortalCustomerPurchase): PortalMixCategory[] {
    if (!purchase.lines || purchase.lines.length === 0) return purchase.categories;
    return [...new Set(purchase.lines.map(mixCategoryOf).filter((key) => key !== null))];
}

/** Mix por categoría desde las líneas: un servicio cuenta en «Servicio», nunca en Accesorios. */
export function purchaseCategoryMix(
    purchases: readonly PortalCustomerPurchase[],
): PortalMixEntry[] {
    const mix = new Map<PortalMixCategory, PortalMixEntry>();
    for (const purchase of purchases) {
        const seen = new Set<PortalMixCategory>();
        for (const line of purchase.lines ?? []) {
            const category = mixCategoryOf(line);
            if (category === null) continue;
            const entry = mix.get(category) ?? { category, purchasesCount: 0, totalSoldCents: 0 };
            entry.totalSoldCents += lineNetCents(line);
            if (!seen.has(category)) entry.purchasesCount += 1;
            seen.add(category);
            mix.set(category, entry);
        }
    }
    return [...mix.values()];
}

export interface CategoryCard {
    key: CategoryKey;
    label: string;
    purchasesCount: number;
    totalSoldCents: number;
}

export function categoryCards(
    mix: readonly PortalMixEntry[],
    totals: { purchasesCount: number; totalSoldCents: number },
): CategoryCard[] {
    const byCategory = new Map(mix.map((entry) => [entry.category, entry]));
    const extra = mix.map((entry) => entry.category).filter((c) => !ALWAYS_SHOWN.includes(c));
    const cards = [...ALWAYS_SHOWN, ...extra].map((category) => ({
        key: category,
        label: CATEGORY_LABEL[category],
        purchasesCount: byCategory.get(category)?.purchasesCount ?? 0,
        totalSoldCents: byCategory.get(category)?.totalSoldCents ?? 0,
    }));
    return [{ key: 'all', label: 'Todos', ...totals }, ...cards];
}

export function filterPurchases(
    purchases: readonly PortalCustomerPurchase[],
    category: CategoryKey,
): PortalCustomerPurchase[] {
    if (category === 'all') return [...purchases];
    return purchases.filter((purchase) => purchaseCategories(purchase).includes(category));
}

export function distinctSkuCount(purchases: readonly PortalCustomerPurchase[]): number {
    return new Set(purchases.filter(hasEquipment).map((purchase) => purchase.sku)).size;
}

export type PurchasesSortKey = 'soldAt' | 'product' | 'payment' | 'soldCents';
export type PurchasesSort = DataTableSort<PurchasesSortKey> & { key: PurchasesSortKey };
export const DEFAULT_PURCHASES_SORT: PurchasesSort = { key: 'soldAt', direction: 'desc' };

function purchaseValue(
    purchase: PortalCustomerPurchase,
    key: PurchasesSortKey,
    category: CategoryKey,
): string | number {
    switch (key) {
        case 'product':
            return ticketHeadline(
                purchase,
                ticketCategoryOf(category),
                productLabel(purchase),
            ).name.toLowerCase();
        case 'payment':
            return paymentChipOf(purchase).label.toLowerCase();
        default:
            return purchase[key];
    }
}

export function sortPurchases(
    purchases: readonly PortalCustomerPurchase[],
    sort: PurchasesSort,
    category: CategoryKey = 'all',
): PortalCustomerPurchase[] {
    return sortBy(purchases, sort.direction, (p) => purchaseValue(p, sort.key, category));
}

export type OtherStoresSortKey =
    | 'storeName'
    | 'purchasesCount'
    | 'totalSoldCents'
    | 'lastPurchaseAt';
export type OtherStoresSort = DataTableSort<OtherStoresSortKey> & { key: OtherStoresSortKey };
export const DEFAULT_OTHER_STORES_SORT: OtherStoresSort = {
    key: 'purchasesCount',
    direction: 'desc',
};

export function sortOtherStores(
    stores: readonly PortalCustomerOtherStore[],
    sort: OtherStoresSort,
): PortalCustomerOtherStore[] {
    return sortBy(stores, sort.direction, (store) =>
        sort.key === 'storeName' ? (store.storeName ?? '').toLowerCase() : store[sort.key],
    );
}

/** El mismo encabezado invierte; uno nuevo arranca A→Z en texto y de mayor a menor lo demás. */
export function nextDetailSort<K extends string>(
    current: { key: K; direction: 'asc' | 'desc' },
    key: K,
    textKeys: readonly K[],
): { key: K; direction: 'asc' | 'desc' } {
    if (current.key === key)
        return { key, direction: current.direction === 'asc' ? 'desc' : 'asc' };
    return { key, direction: textKeys.includes(key) ? 'asc' : 'desc' };
}

function sortBy<T>(
    rows: readonly T[],
    direction: 'asc' | 'desc',
    valueOf: (row: T) => string | number,
): T[] {
    const factor = direction === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
        const av = valueOf(a);
        const bv = valueOf(b);
        if (av === bv) return 0;
        return (av < bv ? -1 : 1) * factor;
    });
}

/** Por qué el cliente es candidato a recompra, con lo que se sabe de sus compras contigo. */
export function repurchaseReason(summary: {
    boughtOnSkCredit: boolean;
    purchasesCount: number;
    daysSinceLastPurchase: number;
}): string {
    const days = String(summary.daysSinceLastPurchase);
    if (summary.boughtOnSkCredit) {
        return `Te compró a crédito SK y lleva ${days} días sin comprarte.`;
    }
    return `Cliente recurrente: ${String(summary.purchasesCount)} compras contigo y ${days} días sin comprarte.`;
}

/** «1 venta» · «3 ventas». */
export function salesCountLabel(count: number): string {
    return count === 1 ? 'venta' : 'ventas';
}

/** Categoría del filtro para las piezas compartidas del ticket; «Todos» no filtra. */
export function ticketCategoryOf(category: CategoryKey): TicketCategory {
    return category === 'all' ? null : category;
}
